import { Button, Grid }                                            from '@mui/material';
import { FastField, Form, Formik, FormikProps }                    from 'formik';
import React, { FC, useEffect, useRef, useState }                  from 'react';
import { NavLink }                                         from 'react-router-dom';
import styled                                                      from 'styled-components';
import { ReactComponent as ChevronRight }                          from '../../../../../../images/icons/icons-white-chevron-right.svg';
import { ColorPicker }                                             from '../../../../../shared/components/ColorPicker/ColorPicker';
import { GridDivider }                                             from '../../../../../shared/components/layout/GridDivider';
import { EAppRoutes, EGender, colors }                             from '../../../../../shared/constants/constants';
import { SelectDateField }                                         from '../../../../../shared/formFields/SelectDateField';
import { SelectField }                                             from '../../../../../shared/formFields/SelectField';
import { TextInputField }                                          from '../../../../../shared/formFields/TextInputField';
import { useFetchCurrentUserProfile, useUpdateCurrentUserProfile } from '../../../../../shared/queries/user';
import { FormSpacing, TextBrand }                                  from '../../../../../shared/styledComponents';
import { FormFooterActions }                                       from '../../../shared/FormFooterActions';
import { LoadingOverlay }                                          from '../../../../../shared/components/LoadingOverlay';
import {
  IInitialValues,
  IMyProfileForm,
  IUserProfile,
  myProfileValidationSchema }                                         from './validation';
import { useNavigationPrompt } from "../../../../../Layout/hooks/useNavigationPrompt";

const WideLink = styled(NavLink)`&& {
  width: 100%;
  color: #2c3f69;
  font-weight: 700;
  padding: 5px 0;
  display: flex;
  align-items: center;
  margin-bottom: 80px;
  justify-content: space-between;
}`;

export const FormButton = styled(Button)`&& {
  border-radius: 9px;
  background-color: #547cd3;
  float: right;
}`;

interface IMyProfileFormProps {
  avatarId?: string;
  currentUser?: IUserProfile;
  selectedFamilyId: string | undefined;
  onColorChange(color: string): void;
}

const SettingOptionTitle = styled(TextBrand)`
  font-weight: 500;
  font-family: 'Jost';
`;

const RequiredAsterisk = styled.span`
  color: #e42e2e;
`;

const ColorProfileContainer = styled(Grid)`&& {
  max-width:400px;
}`;

const getInitialValues = (currentUser: IUserProfile) => ({
  ...currentUser,
  fullName : currentUser?.firstName && currentUser?.lastName ? `${ currentUser?.firstName } ${ currentUser?.lastName.trim() }` : '',
  color    : currentUser?.color ?? '#547cd3',
});

const isFormTouched = (touched: Record<string, any>): boolean => Object.values(touched).some(value => value === true);

export const MyProfileForm: FC<IMyProfileFormProps> = ({
  avatarId,
  selectedFamilyId,
  onColorChange,
}) => {
  const formikRef = useRef<FormikProps<IInitialValues> | null>(null);
  const { data: userProfile, isLoading: loadingProfile, refetch } = useFetchCurrentUserProfile(selectedFamilyId);
  const { mutate: updateUserProfile, isLoading: updatingUserProfile } = useUpdateCurrentUserProfile();
  const [ actualFormValues, setActualFormValues ] = useState<IInitialValues>({});

  useEffect(()=> {
    if (userProfile) {
      const initialValues = getInitialValues(userProfile);
      setActualFormValues(initialValues);
    }
  }, [userProfile]);

  useEffect(() => {
    const formAvatarId = formikRef?.current?.values?.avatarId;

    if (avatarId !== formAvatarId) {
      formikRef.current?.setFieldValue('avatarId', avatarId);
      formikRef.current?.setFieldTouched('avatarId', true);
    }
  }, [ avatarId ]);

  const updateProfile = async () => {
    const formValues = formikRef.current?.values;
    const nameParts = formValues?.fullName?.split(/\s+/) || [];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
    const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '';
    updateUserProfile({
      avatarId    : userProfile?.avatar?.id,
      color       : formValues?.color,
      firstName,
      lastName    : middleName ? `${middleName} ${lastName}` : lastName,
      gender      : formValues?.gender,
      dateOfBirth : formValues?.dateOfBirth,
      country     : userProfile?.country || 'US',
    } as IMyProfileForm, {
      onSuccess: () => {
        if (formValues) {
          setActualFormValues(formValues);
        }
      },
    });
  };

  const FormContent = ({
    values,
    dirty,
    touched,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
  }: FormikProps<IInitialValues>) => {
      useNavigationPrompt(
          isFormTouched(touched),
          `Are you sure you want to leave this page?\nUnsaved changes in the Customer Info would be lost.`
      );
     return (
          <>
              <Form onSubmit={handleSubmit}>
                  <FormSpacing>
                      <Grid
                          container
                          spacing={3}
                      >
                          <Grid
                              item
                              xs={12}
                              sm={6}
                              md={4}
                          >
                              <FastField
                                  label={<>Full Name <RequiredAsterisk>*</RequiredAsterisk></>}
                                  name={'fullName'}
                                  inputProps={{ maxLength: 100 }}
                                  component={TextInputField}
                              />
                          </Grid>

                          <Grid
                              item
                              xs={12}
                              sm={6}
                              md={4}
                          >
                              <FastField
                                  label={<>Date of Birth <RequiredAsterisk>*</RequiredAsterisk></>}
                                  name={'dateOfBirth'}
                                  minDate={new Date(1900, 1, 1)}
                                  maxDate={new Date()}
                                  component={SelectDateField}
                              />
                          </Grid>

                          <Grid
                              item
                              xs={6}
                              md={4}
                          >
                              <FastField
                                  label={<>Gender <RequiredAsterisk>*</RequiredAsterisk></>}
                                  name={'gender'}
                                  options={[EGender.Male, EGender.Female]}
                                  component={SelectField}
                              />
                          </Grid>

                          <GridDivider margin="10px 0" />

                          <ColorProfileContainer
                              item
                          >
                              <span>Color Profile</span>

                              <ColorPicker
                                margin="15px 0 0"
                                selectedColor={values?.color}
                                setSelectedColor={(color: string) => {
                                  setFieldValue('color', color);
                                  setFieldTouched('color', true);
                                  onColorChange(color);
                                }}
                              />
                            </ColorProfileContainer>

                          <GridDivider margin="10px 0 0" />

                          <Grid
                              item
                              xs={12}
                          >
                              <WideLink to={`${EAppRoutes.Settings}/my-profile/about-me`}>
                                  <SettingOptionTitle>About Me</SettingOptionTitle>
                                  <ChevronRight />
                              </WideLink>
                          </Grid>
                      </Grid>
                  </FormSpacing>
                  <FormFooterActions
                      dirty={dirty}
                      isLoading={updatingUserProfile}
                      formikRef={formikRef}
                  />
              </Form>
              <LoadingOverlay loading={loadingProfile || updatingUserProfile} />
          </>
      );
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formikRef}
      initialValues={actualFormValues}
      validationSchema={myProfileValidationSchema}
      onSubmit={updateProfile}
    >
      { (props) => <FormContent {...props} /> }
    </Formik>
  );
};
